"use client";

import { forwardRef, useId, useMemo, useState } from "react";
import DropdownMenu from "../DropdownMenu/DropdownMenu.jsx";
import TextField from "../TextField/TextField.jsx";
import styles from "./Select.module.css";

const sizeClass = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
};

const ChevronDown = (
  <svg
    viewBox="0 0 24 24"
    width="100%"
    height="100%"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SearchIcon = (
  <svg
    viewBox="0 0 24 24"
    width="100%"
    height="100%"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

/**
 * Select — picks a single value from a list. Visually shaped like a form
 * input; opens a DropdownMenu with the current value highlighted.
 *
 * Pass `searchable` to add a search input at the top of the menu — the menu
 * also matches the trigger width in that mode (you can opt into matched-width
 * separately via `matchTriggerWidth` when not searchable).
 *
 * For visible "all options at once" pickers (e.g. tab strips), use
 * SegmentedControl instead.
 */
const Select = forwardRef(function Select(
  {
    label,
    helperText,
    value,
    onChange,
    options,
    placeholder = "Select…",
    size = "md",
    disabled = false,
    fullWidth = true,
    align = "start",
    searchable = false,
    matchTriggerWidth = false,
    searchPlaceholder = "Search…",
    searchKbd,
    className = "",
    id,
    "aria-label": ariaLabel,
  },
  ref
) {
  const fallbackId = useId();
  const triggerId = id ?? fallbackId;
  const [searchQuery, setSearchQuery] = useState("");

  const currentOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    if (!searchable) return options;
    const trimmed = searchQuery.trim().toLowerCase();
    if (!trimmed) return options;
    return options.filter((option) => {
      if (option.type === "divider" || option.type === "section") return true;
      return String(option.label).toLowerCase().includes(trimmed);
    });
  }, [options, searchQuery, searchable]);

  const items = useMemo(
    () =>
      filteredOptions.map((option) => {
        if (option.type === "divider" || option.type === "section") return option;
        return {
          id: String(option.value),
          label: option.label,
          selected: option.value === value,
          disabled: option.disabled,
          onSelect: () => onChange?.(option.value),
        };
      }),
    [filteredOptions, value, onChange]
  );

  const fieldClasses = [styles.field, fullWidth ? "" : styles.fitContent]
    .filter(Boolean)
    .join(" ");

  const triggerClasses = [
    styles.trigger,
    sizeClass[size] ?? sizeClass.md,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const menuHeader = searchable ? (
    <TextField
      placeholder={searchPlaceholder}
      value={searchQuery}
      onChange={(event) => setSearchQuery(event.target.value)}
      size="sm"
      iconLeft={SearchIcon}
      kbd={searchKbd}
      autoFocus
      onKeyDown={(event) => {
        // Block Escape from bubbling to DropdownMenu's close handler when
        // there's text in the search — first Escape clears the search,
        // second closes the menu (native).
        if (event.key === "Escape" && searchQuery) {
          event.stopPropagation();
          setSearchQuery("");
        }
      }}
    />
  ) : null;

  const itemsHaveSelectableContent = items.some(
    (item) => item.type !== "divider" && item.type !== "section"
  );

  return (
    <div className={fieldClasses}>
      {label ? (
        <label htmlFor={triggerId} className={styles.label}>
          {label}
        </label>
      ) : null}

      <DropdownMenu
        align={align}
        items={items}
        matchTriggerWidth={searchable || matchTriggerWidth}
        header={menuHeader}
        emptyState={
          searchable && !itemsHaveSelectableContent ? "No matches" : undefined
        }
        /* Cap searchable menus to ~5 items + the search header so the list
           never sprawls down a long viewport. */
        maxHeight={searchable ? 220 : undefined}
        onOpenChange={(nextIsOpen) => {
          if (!nextIsOpen) setSearchQuery("");
        }}
        trigger={
          <button
            ref={ref}
            id={triggerId}
            type="button"
            disabled={disabled}
            className={triggerClasses}
            aria-label={ariaLabel ?? (typeof label === "string" ? label : undefined)}
          >
            <span
              className={`${styles.triggerLabel} ${currentOption ? "" : styles.triggerLabelEmpty}`}
            >
              {currentOption ? currentOption.label : placeholder}
            </span>
            <span className={styles.chevron}>{ChevronDown}</span>
          </button>
        }
      />

      {helperText ? <div className={styles.helperText}>{helperText}</div> : null}
    </div>
  );
});

export default Select;
